import {ProcedureFlowchart, ProcedureMetadata} from 'procedures/ProcedureDomainTypes';

export enum ProceduresActionType {
  // These actions are named to indicate that they are for /legacy/ procedures, to distinguish them from PoW
  UPDATED_FLOWCHART = 'LEGACY_UPDATED_FLOWCHART',
  RELEASED_FLOWCHART = 'LEGACY_RELEASED_FLOWCHART',
  DIRTY_STATE_CHANGED = 'LEGACY_DIRTY_STATE_CHANGED',
  READONLY_STATE_CHANGED = 'LEGACY_READONLY_STATE_CHANGED',
  IS_SAVE_IN_FLIGHT_STATE_CHANGED = 'LEGACY_IS_SAVE_IN_FLIGHT_STATE_CHANGED',
  AUTOSAVER_PAUSED_CHANGED = 'LEGACY_AUTOSAVER_PAUSED_CHANGED',
  AUTOSAVER_BLOCKED_CHANGED = 'LEGACY_AUTOSAVER_BLOCKED_CHANGED',
}

type _UpdatedFlowchartData =
  // Caller can provide one or both of `flowchart` or `procedureMetadata`
  | {
      procedureId: string;
      flowchart: ProcedureFlowchart;
      procedureMetadata?: ProcedureMetadata;
    }
  | {
      procedureId: string;
      flowchart?: ProcedureFlowchart;
      procedureMetadata: ProcedureMetadata;
    };

type ProceduresActionUpdatedFlowchart = {
  type: ProceduresActionType.UPDATED_FLOWCHART;
} & _UpdatedFlowchartData;

interface ProceduresActionReleasedFlowchart {
  type: ProceduresActionType.RELEASED_FLOWCHART;
  procedureId: string;
}

interface ProceduresBooleanPropertyChanged {
  type:
    | ProceduresActionType.DIRTY_STATE_CHANGED
    | ProceduresActionType.READONLY_STATE_CHANGED
    | ProceduresActionType.IS_SAVE_IN_FLIGHT_STATE_CHANGED
    | ProceduresActionType.AUTOSAVER_PAUSED_CHANGED
    | ProceduresActionType.AUTOSAVER_BLOCKED_CHANGED;
  procedureId: string;
  value: boolean;
}

export type ProceduresAction =
  | ProceduresActionUpdatedFlowchart
  | ProceduresActionReleasedFlowchart
  | ProceduresBooleanPropertyChanged;

export function procedureFlowchartUpdated(params: _UpdatedFlowchartData): ProceduresActionUpdatedFlowchart {
  return {
    ...params,
    type: ProceduresActionType.UPDATED_FLOWCHART,
  };
}

export function procedureFlowchartReleased(procedureId: string): ProceduresActionReleasedFlowchart {
  return {
    type: ProceduresActionType.RELEASED_FLOWCHART,
    procedureId,
  };
}

export function procedureDirtyStateChanged(
  procedureId: string,
  isDirty: boolean
): ProceduresBooleanPropertyChanged {
  return {
    type: ProceduresActionType.DIRTY_STATE_CHANGED,
    procedureId,
    value: isDirty,
  };
}

export function procedureReadonlyStateChanged(
  procedureId: string,
  isReadonly: boolean
): ProceduresBooleanPropertyChanged {
  return {
    type: ProceduresActionType.READONLY_STATE_CHANGED,
    procedureId,
    value: isReadonly,
  };
}

export function procedureIsSaveInFlightStateChanged(
  procedureId: string,
  isSaveInFlight: boolean
): ProceduresBooleanPropertyChanged {
  return {
    type: ProceduresActionType.IS_SAVE_IN_FLIGHT_STATE_CHANGED,
    procedureId,
    value: isSaveInFlight,
  };
}

export function procedureAutosaverPausedChanged(
  procedureId: string,
  isAutosaverPaused: boolean
): ProceduresBooleanPropertyChanged {
  return {
    type: ProceduresActionType.AUTOSAVER_PAUSED_CHANGED,
    procedureId,
    value: isAutosaverPaused,
  };
}

export function procedureAutosaverBlockedChanged(
  procedureId: string,
  isAutosaverBlocked: boolean
): ProceduresBooleanPropertyChanged {
  return {
    type: ProceduresActionType.AUTOSAVER_BLOCKED_CHANGED,
    procedureId,
    value: isAutosaverBlocked,
  };
}
